import "./index.css";

import { StrictMode } from "react";

import { AsyncProviderConfig, asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { SnackbarProvider } from "@dexibit/core";
import { gaeaThemeLight, ThemeProvider } from "@dexibit/styles";

import App from "./App";
import ErrorFallback from "./components/ErrorFallback";
import { NavigationHelmet } from "./NavigationHelmet";
import { store } from "./store/store";
import logger from "./utils/logger";
import initDatadog from "./utils/logger/datadog";
import tokenManager from "./utils/tokenManager";
import toUserContext from "./utils/toUserContext";

// Ensure to initialize datadog as early as possible
initDatadog();

if (!APP_LD_CLIENT_SIDE_ID) {
  throw new Error("LaunchDarkly client-side ID not found in environment variables");
}

void (async () => {
  const container = document.getElementById("root");

  if (!container) {
    throw new Error("Could not find root container");
  }

  const root = createRoot(container);

  const userData = tokenManager.getUserData();
  const context = toUserContext(userData);

  const launchDarklyConfig: AsyncProviderConfig = {
    clientSideID: APP_LD_CLIENT_SIDE_ID,
    options: {
      // Use our logger so it gets to our puppy DataDog as well
      logger,
    },
    context,
  };

  const LDProvider = await asyncWithLDProvider(launchDarklyConfig);

  root.render(
    <StrictMode>
      <HelmetProvider>
        <ThemeProvider theme={gaeaThemeLight}>
          <SnackbarProvider>
            <Provider store={store}>
              <NavigationHelmet />
              <BrowserRouter>
                <LDProvider>
                  <ErrorBoundary
                    FallbackComponent={ErrorFallback}
                    onError={(error: Error) => logger.error("Global error", error)}
                  >
                    <App />
                  </ErrorBoundary>
                </LDProvider>
              </BrowserRouter>
            </Provider>
          </SnackbarProvider>
        </ThemeProvider>
      </HelmetProvider>
    </StrictMode>,
  );
})();
